// Global Instructions Rule Applied!
import React, { forwardRef, useImperativeHandle, useState, useMemo, useCallback, useEffect, memo } from 'react'
import { InputManager } from '../../../core/components/InputManager/InputManager.tsx'
import { FieldConfig, FieldTypeEnum } from '../../../core/components/InputManager/types.ts'
import { TaskRecordFields, validateTaskRecord, type TaskRecord } from '../../../state_manager/data_models/task_record.ts'
import { UserEntryFields } from '../../../state_manager/data_models/user_entry.ts'
import dayjs from 'dayjs'

/** An assignee as ContactInput emits it: the option plus the contact's email when known. */
export interface TaskFormAssignee {
  label: string
  value: string
  email?: string | null
}

export interface TaskFormValues {
  subject: string
  completed: boolean
  assignedTo: Array<string | TaskFormAssignee>
  dueDate: any | null
  notes: string
}

export interface TaskFormProps {
  initialValues?: Partial<TaskFormValues>
  onValuesChange?: (values: Partial<TaskFormValues>) => void
  selectedContacts?: Array<{ label: string; value: string }>
  onContactsChange?: (contacts: Array<{ label: string; value: string }>) => void
  className?: string
  style?: React.CSSProperties
}

export interface TaskFormRef {
  /** Validates and returns the task as a TaskRecord (not the raw form values). */
  validateFields: () => Promise<TaskRecord>
  getFieldsValue: () => TaskFormValues
  setFieldsValue: (values: Partial<TaskFormValues>) => void
  resetFields: () => void
  getFieldValue: (name: string) => any
  setFieldValue: (name: string, value: any) => void
}

export const TaskForm = forwardRef<TaskFormRef, TaskFormProps>(
  (
    { initialValues = {}, onValuesChange, selectedContacts = [], onContactsChange, className = '', style = {} },
    ref
  ) => {
    const [fieldValues, setFieldValues] = useState<Partial<TaskFormValues>>({
      subject: '',
      completed: false,
      assignedTo: [],
      dueDate: null,
      notes: '',
      ...initialValues
    })

    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

    useEffect(() => {
      if (initialValues) {
        setFieldValues((prev) => ({ ...prev, ...initialValues }))
      }
    }, [initialValues])

    const handleFieldChange = useCallback(
      (key: string, value: any) => {
        setFieldValues((prev) => {
          const newValues = { ...prev, [key]: value }

          // Clear validation error for this field when it changes
          setValidationErrors((prev) => {
            const newErrors = { ...prev }
            delete newErrors[key]
            return newErrors
          })

          onValuesChange?.(newValues)
          return newValues
        })
      },
      [onValuesChange]
    )

    const validateFields = useCallback(async () => {
      const { subject, completed, assignedTo, dueDate, notes } = fieldValues
      const errors: Record<string, string> = {}

      if (!subject || subject.trim() === '') {
        errors.subject = 'Please enter a subject'
      }

      if (subject && subject.length > 80) {
        errors.subject = 'Subject cannot exceed 80 characters'
      }

      setValidationErrors(errors)

      if (Object.keys(errors).length > 0) {
        throw new Error(errors.subject || 'Validation failed')
      }

      // Transform assignedTo array to UserEntry format
      const assignees = (assignedTo || []).map((contact) => {
        if (typeof contact === 'string') {
          return {
            [UserEntryFields.contacts__id]: contact,
            [UserEntryFields.contacts__email]: null,
            [UserEntryFields.prog__display_name]: null,
            [UserEntryFields.contacts__first_name]: null,
            [UserEntryFields.contacts__last_name]: null
          }
        }
        // Extract name parts from label if possible
        const label = contact.label || ''
        const nameParts = label.split(' ')
        const firstName = nameParts[0] || null
        const lastName = nameParts.slice(1).join(' ') || null

        return {
          [UserEntryFields.contacts__id]: contact.value || null,
          [UserEntryFields.contacts__email]: contact.email || null,
          [UserEntryFields.prog__display_name]: label || null,
          [UserEntryFields.contacts__first_name]: firstName || null,
          [UserEntryFields.contacts__last_name]: lastName || null
        }
      })

      // Return object with prefixed field names using TaskRecordFields constants
      const taskRecord = {
        [TaskRecordFields.tasks__id]: crypto.randomUUID(),
        [TaskRecordFields.tasks__subject]: subject || '',
        [TaskRecordFields.tasks__completed]: completed || false,
        [TaskRecordFields.prog__task_assignees]: assignees,
        [TaskRecordFields.tasks__due_by]: dueDate
          ? dayjs.isDayjs(dueDate)
            ? dueDate.format('YYYY-MM-DD')
            : dayjs(dueDate).format('YYYY-MM-DD')
          : null,
        [TaskRecordFields.tasks__notes]: notes || ''
      }

      // Validate using schema before returning
      try {
        return validateTaskRecord(taskRecord)
      } catch (error) {
        throw new Error(`Task validation failed: ${error instanceof Error ? error.message : String(error)}`)
      }
    }, [fieldValues])

    useImperativeHandle(ref, () => ({
      validateFields,
      getFieldsValue: () => fieldValues as TaskFormValues,
      setFieldsValue: (values: Partial<TaskFormValues>) => {
        setFieldValues((prev) => ({ ...prev, ...values }))
      },
      resetFields: () => {
        setFieldValues({
          subject: '',
          completed: false,
          assignedTo: [],
          dueDate: null,
          notes: ''
        })
        setValidationErrors({})
      },
      getFieldValue: (name: string) => fieldValues[name as keyof TaskFormValues],
      setFieldValue: (name: string, value: any) => {
        setFieldValues((prev) => ({ ...prev, [name]: value }))
      }
    }))

    const nonNotesFields: FieldConfig[] = useMemo(
      () => [
        {
          key: 'subject',
          type: FieldTypeEnum.TEXTAREA,
          value: fieldValues.subject || '',
          required: true,
          placeholder: 'Add task here',
          maxLength: 80,
          autoSize: { minRows: 1, maxRows: 4 },
          size: 'middle',
          autoFocus: true
        },
        {
          key: 'completed',
          label: 'Complete',
          type: FieldTypeEnum.SWITCH,
          value: fieldValues.completed || false,
          required: false,
          size: 'middle'
        },
        {
          key: 'assignedTo',
          label: 'Assigned To',
          type: FieldTypeEnum.CONTACT,
          value: fieldValues.assignedTo || [],
          required: false,
          placeholder: 'Search contacts...',
          mode: 'multiple',
          allowAddNew: true,
          size: 'small'
        },
        {
          key: 'dueDate',
          label: 'Due Date',
          type: FieldTypeEnum.DATE,
          value: fieldValues.dueDate || null,
          required: false,
          format: 'YYYY-MM-DD',
          size: 'small'
        }
      ],
      [fieldValues.subject, fieldValues.completed, fieldValues.assignedTo, fieldValues.dueDate]
    )

    const notesField: FieldConfig = useMemo(
      () => ({
        key: 'notes',
        label: 'Notes',
        type: FieldTypeEnum.RICHTEXT,
        value: fieldValues.notes || '',
        required: false,
        placeholder: 'Enter task notes...',
        className: 'flex-1',
        style: { display: 'flex', flexDirection: 'column', minHeight: 0 }
      }),
      [fieldValues.notes]
    )

    const notesValidationErrors = useMemo((): Record<string, string> => {
      const notesError = validationErrors.notes
      return notesError ? { notes: notesError } : {}
    }, [validationErrors.notes])

    const notesFieldsArray = useMemo(() => [notesField], [notesField])

    const notesInputStyle = useMemo<React.CSSProperties>(
      () => ({ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }),
      []
    )

    const subjectField = nonNotesFields[0]
    const completedField = nonNotesFields[1]
    const assignedToField = nonNotesFields[2]
    const dueDateField = nonNotesFields[3]

    return (
      <div className={`h-full flex flex-col pr-1 ${className}`} style={style}>
        <div className='flex-none'>
          {/* Subject field - no label */}
          <div className='mt-1 mb-3'>
            <InputManager
              inputMode='edit'
              fields={[subjectField]}
              labelPosition='none'
              onChange={handleFieldChange}
              fieldWrapping={true}
              validationErrors={validationErrors}
            />
          </div>

          {/* Complete, Assigned To, and Due Date fields in a row */}
          <div className='task-form-fields-row mb-3'>
            <div className='w-full'>
              <InputManager
                inputMode='edit'
                fields={[completedField]}
                labelPosition='above'
                onChange={handleFieldChange}
                fieldWrapping={true}
                validationErrors={validationErrors}
              />
            </div>
            <div className='w-full'>
              <InputManager
                inputMode='edit'
                fields={[assignedToField]}
                labelPosition='above'
                onChange={handleFieldChange}
                fieldWrapping={true}
                validationErrors={validationErrors}
              />
            </div>
            <div className='w-full'>
              <InputManager
                inputMode='edit'
                fields={[dueDateField]}
                labelPosition='above'
                onChange={handleFieldChange}
                fieldWrapping={true}
                validationErrors={validationErrors}
              />
            </div>
          </div>
        </div>

        {/* Notes field - takes up remaining space */}
        <div
          className='flex-1 flex flex-col min-h-0'
          style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}
        >
          <div className='mb-2'>
            <span className='text-sm font-bold text-gray-800'>Notes:</span>
          </div>
          <div
            className='flex-1 min-h-0'
            style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}
          >
            <NotesInputManager
              fields={notesFieldsArray}
              onChange={handleFieldChange}
              validationErrors={notesValidationErrors}
              className='h-full'
              style={notesInputStyle}
            />
          </div>
        </div>
      </div>
    )
  }
)

TaskForm.displayName = 'TaskForm'

// Memoized wrapper for notes InputManager to prevent re-renders when other fields change
const NotesInputManager = memo<{
  fields: FieldConfig[]
  onChange: (key: string, value: any) => void
  validationErrors: Record<string, string>
  className?: string
  style?: React.CSSProperties
}>(
  ({ fields, onChange, validationErrors, className, style }) => {
    return (
      <InputManager
        inputMode='edit'
        fields={fields}
        labelPosition='none'
        onChange={onChange}
        className={className}
        style={style}
        fieldWrapping={true}
        validationErrors={validationErrors}
      />
    )
  },
  (prevProps, nextProps) => {
    // Only re-render if notes-specific props change
    return (
      prevProps.fields === nextProps.fields &&
      prevProps.onChange === nextProps.onChange &&
      prevProps.validationErrors === nextProps.validationErrors &&
      prevProps.className === nextProps.className &&
      prevProps.style === nextProps.style
    )
  }
)

NotesInputManager.displayName = 'NotesInputManager'
